import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// UX-TR-01 §0: a dedicated data context for the Trainer App — same shape as DeskDataContext
// (loads `/me` once, bounces anyone who isn't this persona), plus its own `/trainer/me` call to
// resolve the caller's own provider identity (name, branch, commission rule) up front so every
// screen below can render without re-fetching it.
const TrainerDataContext = createContext(null);

export function TrainerDataProvider({ children }) {
  const [me, setMe] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await api.get("/me");
      if (meRes.data.user_type === "kairoo") { navigate("/kairoo/dashboard"); return; }
      if (["front_of_house_fd", "front_of_house_mgr"].includes(meRes.data.role)) { navigate("/desk/today"); return; }
      if (meRes.data.role !== "trainer") { navigate("/app/dashboard"); return; }
      const providerRes = await api.get("/trainer/me");
      setMe(meRes.data);
      setProvider(providerRes.data);
    } catch (e) {
      if (e.response?.status === 401) { navigate("/login"); return; }
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    navigate("/login");
  }, [navigate]);

  return (
    <TrainerDataContext.Provider value={{ me, provider, loading, error, reload: load, logout }}>
      {children}
    </TrainerDataContext.Provider>
  );
}

export function useTrainerData() {
  return useContext(TrainerDataContext);
}
